import React, { useState, useEffect, useRef } from 'react';
import { tasksApi } from '../../api/tasks';
import { adminApi } from '../../api/admin';
import toast from 'react-hot-toast';
import {
  X, CheckCircle2, Circle, Clock, User, AlertTriangle, Send, Paperclip,
  Download, Trash2, Pause, Play, MessageSquare, FileText, Calendar, Tag, ExternalLink
} from 'lucide-react';

const PRIORITY_COLORS = {
  urgent: 'bg-red-100 text-red-700 border-red-200',
  high: 'bg-orange-100 text-orange-700 border-orange-200',
  normal: 'bg-blue-100 text-blue-700 border-blue-200',
  low: 'bg-gray-100 text-gray-600 border-gray-200',
};

const STATUS_CONFIG = {
  pending: { label: 'Pending', color: 'bg-gray-100 text-gray-700', icon: Circle },
  in_progress: { label: 'In Progress', color: 'bg-blue-100 text-blue-700', icon: Play },
  on_hold: { label: 'On Hold', color: 'bg-yellow-100 text-yellow-700', icon: Pause },
  blocked: { label: 'Blocked', color: 'bg-red-100 text-red-700', icon: AlertTriangle },
  completed: { label: 'Completed', color: 'bg-green-100 text-green-700', icon: CheckCircle2 },
};

function formatFileSize(bytes) {
  if (!bytes) return '';
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / 1048576).toFixed(1) + ' MB';
}

export default function TaskDetailModal({ task, allTasks, onClose, onRefresh, onJumpToTab, tabs }) {
  const [comments, setComments] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [users, setUsers] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [loadingComments, setLoadingComments] = useState(true);
  const [loadingAttachments, setLoadingAttachments] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [editField, setEditField] = useState(null);
  const [editValue, setEditValue] = useState('');
  const fileInputRef = useRef(null);
  const [activeSection, setActiveSection] = useState('comments');

  const taskId = 'T-' + String(task.task_number || 0).padStart(4, '0');
  const isCompleted = task.status === 'completed';

  useEffect(() => {
    fetchComments();
    fetchAttachments();
    adminApi.getUsers().then(res => setUsers(res.data.users || [])).catch(() => {});
  }, [task.id]);

  const fetchComments = async () => {
    setLoadingComments(true);
    try {
      const res = await tasksApi.getComments(task.id);
      setComments(res.data.comments);
    } catch (e) {}
    finally { setLoadingComments(false); }
  };

  const fetchAttachments = async () => {
    setLoadingAttachments(true);
    try {
      const res = await tasksApi.getAttachments(task.id);
      setAttachments(res.data.attachments);
    } catch (e) {}
    finally { setLoadingAttachments(false); }
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) return;
    try {
      const res = await tasksApi.addComment(task.id, newComment);
      setComments([...comments, res.data.comment]);
      setNewComment('');
    } catch (e) { toast.error('Failed to add comment'); }
  };

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const res = await tasksApi.uploadAttachment(task.id, file);
      setAttachments([...attachments, res.data.attachment]);
      toast.success('File uploaded');
    } catch (e) { toast.error('Upload failed'); }
    finally { setUploading(false); fileInputRef.current.value = ''; }
  };

  const handleDeleteAttachment = async (attId) => {
    if (!window.confirm('Delete this attachment?')) return;
    try {
      await tasksApi.deleteAttachment(task.id, attId);
      setAttachments(attachments.filter(a => a.id !== attId));
      toast.success('Attachment deleted');
    } catch (e) { toast.error('Failed to delete'); }
  };

  const handleStatusChange = async (status) => {
    try {
      if (status === 'completed') {
        await tasksApi.complete(task.id, {});
      } else {
        await tasksApi.setStatus(task.id, status);
      }
      toast.success('Status updated');
      onRefresh();
    } catch (err) {
      if (err.response?.status === 409 && err.response?.data?.warning) {
        if (window.confirm(err.response.data.message + '\n\nComplete anyway?')) {
          await tasksApi.complete(task.id, { force: true });
          toast.success('Task completed');
          onRefresh();
        }
      } else {
        toast.error(err.response?.data?.error || 'Failed');
      }
    }
  };

  const handleUpdate = async (field, value) => {
    try {
      await tasksApi.update(task.id, { [field]: value });
      toast.success('Updated');
      setEditField(null);
      onRefresh();
    } catch (e) { toast.error('Failed to update'); }
  };

  const incompleteDeps = (task.depends_on || []).map(depId => {
    const dep = (allTasks || []).find(t => t.id === depId);
    return dep ? { title: dep.title, completed: dep.status === 'completed', id: dep.id } : null;
  }).filter(Boolean);

  const sc = STATUS_CONFIG[task.status] || STATUS_CONFIG.pending;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-start justify-between px-6 py-4 border-b border-gray-100">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-xs font-mono px-2 py-0.5 rounded bg-gray-100 text-gray-500">{taskId}</span>
              <span className={'text-xs px-2 py-0.5 rounded font-medium ' + (PRIORITY_COLORS[task.priority] || PRIORITY_COLORS.normal)}>{task.priority}</span>
              <span className={'text-xs px-2 py-0.5 rounded font-medium ' + sc.color}>{sc.label}</span>
              {task.auto_generated && <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-400">auto</span>}
            </div>
            <h2 className="text-lg font-semibold text-gray-900">{task.title}</h2>
            {task.description && <p className="text-sm text-gray-500 mt-1">{task.description}</p>}
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 ml-4">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body — scrollable */}
        <div className="flex-1 overflow-y-auto">
          <div className="grid grid-cols-3 gap-0">
            {/* Left column — main content */}
            <div className="col-span-2 border-r border-gray-100">
              {/* Dependencies */}
              {incompleteDeps.length > 0 && (
                <div className="px-6 py-3 border-b border-gray-50 bg-amber-50/50">
                  <span className="text-xs font-medium text-amber-700 flex items-center gap-1 mb-1">
                    <AlertTriangle className="h-3 w-3" /> Dependencies
                  </span>
                  {incompleteDeps.map(dep => (
                    <div key={dep.id} className="flex items-center gap-2 ml-4 mt-1">
                      {dep.completed
                        ? <CheckCircle2 className="h-3.5 w-3.5 text-green-500" />
                        : <Circle className="h-3.5 w-3.5 text-amber-400" />}
                      <span className={'text-xs ' + (dep.completed ? 'text-gray-400 line-through' : 'text-amber-700')}>{dep.title}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Section tabs */}
              <div className="flex border-b border-gray-100 px-6">
                {[
                  { id: 'comments', label: 'Comments', icon: MessageSquare, count: comments.length },
                  { id: 'attachments', label: 'Attachments', icon: Paperclip, count: attachments.length },
                ].map(tab => (
                  <button key={tab.id} onClick={() => setActiveSection(tab.id)}
                    className={'flex items-center gap-1.5 px-3 py-2.5 text-xs font-medium border-b-2 transition-colors ' +
                      (activeSection === tab.id ? 'border-brand-600 text-brand-700' : 'border-transparent text-gray-500 hover:text-gray-700')}>
                    <tab.icon className="h-3.5 w-3.5" />
                    {tab.label} {tab.count > 0 && <span className="text-[10px] bg-gray-100 px-1.5 rounded-full">{tab.count}</span>}
                  </button>
                ))}
              </div>

              {/* Comments section */}
              {activeSection === 'comments' && (
                <div className="px-6 py-4">
                  {loadingComments ? <p className="text-xs text-gray-400">Loading...</p> : (
                    <div className="space-y-3 mb-4">
                      {comments.map(c => (
                        <div key={c.id} className="flex gap-3">
                          <div className="w-7 h-7 rounded-full bg-brand-100 text-brand-700 flex items-center justify-center text-[10px] font-semibold flex-shrink-0">
                            {(c.first_name?.[0] || '') + (c.last_name?.[0] || '')}
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <span className="text-xs font-medium text-gray-900">{c.first_name} {c.last_name}</span>
                              <span className="text-[10px] text-gray-400">{new Date(c.created_at).toLocaleString()}</span>
                            </div>
                            <p className="text-sm text-gray-700 mt-0.5">{c.content}</p>
                          </div>
                        </div>
                      ))}
                      {comments.length === 0 && <p className="text-xs text-gray-400 text-center py-4">No comments yet</p>}
                    </div>
                  )}
                  <div className="flex gap-2">
                    <input type="text" value={newComment} onChange={e => setNewComment(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') handleAddComment(); }}
                      placeholder="Write a comment..."
                      className="flex-1 text-sm px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-brand-500" />
                    <button onClick={handleAddComment} disabled={!newComment.trim()}
                      className="px-3 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 disabled:opacity-30">
                      <Send className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              )}

              {/* Attachments section */}
              {activeSection === 'attachments' && (
                <div className="px-6 py-4">
                  <div className="space-y-2 mb-4">
                    {loadingAttachments ? <p className="text-xs text-gray-400">Loading...</p> : (
                      <>
                        {attachments.map(att => (
                          <div key={att.id} className="flex items-center gap-3 px-3 py-2.5 bg-gray-50 rounded-lg border border-gray-100 group">
                            <FileText className="h-5 w-5 text-gray-400 flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm text-gray-900 truncate">{att.original_name}</p>
                              <div className="flex items-center gap-2 text-[10px] text-gray-400">
                                <span>{formatFileSize(att.size_bytes)}</span>
                                <span>{att.first_name} {att.last_name}</span>
                                <span>{new Date(att.created_at).toLocaleDateString()}</span>
                              </div>
                            </div>
                            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                              <a href={'/api/tasks/' + task.id + '/attachments/' + att.id + '/download'}
                                className="p-1 text-gray-400 hover:text-brand-600" title="Download">
                                <Download className="h-4 w-4" />
                              </a>
                              <button onClick={() => handleDeleteAttachment(att.id)}
                                className="p-1 text-gray-400 hover:text-red-600" title="Delete">
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                          </div>
                        ))}
                        {attachments.length === 0 && <p className="text-xs text-gray-400 text-center py-4">No attachments</p>}
                      </>
                    )}
                  </div>
                  <input type="file" ref={fileInputRef} onChange={handleUpload} className="hidden" />
                  <button onClick={() => fileInputRef.current?.click()} disabled={uploading}
                    className="flex items-center gap-2 px-3 py-2 text-sm text-brand-600 border border-brand-200 rounded-lg hover:bg-brand-50 disabled:opacity-50">
                    <Paperclip className="h-4 w-4" />
                    {uploading ? 'Uploading...' : 'Attach File'}
                  </button>
                </div>
              )}
            </div>

            {/* Right sidebar — properties */}
            <div className="px-4 py-4 space-y-4">
              {/* Status */}
              <div>
                <label className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Status</label>
                <div className="flex flex-wrap gap-1 mt-1.5">
                  {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
                    <button key={key} onClick={() => handleStatusChange(key)}
                      className={'text-[10px] px-2 py-1 rounded-md border transition-colors ' +
                        (task.status === key ? cfg.color + ' border-current font-semibold' : 'border-gray-200 text-gray-500 hover:border-gray-300')}>
                      {cfg.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Assignee */}
              <div>
                <label className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Assignee</label>
                <select value={task.assigned_to || ''} onChange={e => handleUpdate('assigned_to', e.target.value || null)}
                  className="mt-1.5 w-full text-xs px-2 py-1.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-brand-500">
                  <option value="">Unassigned</option>
                  {users.map(u => <option key={u.id} value={u.id}>{u.first_name} {u.last_name}</option>)}
                </select>
              </div>

              {/* Priority */}
              <div>
                <label className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Priority</label>
                <select value={task.priority} onChange={e => handleUpdate('priority', e.target.value)}
                  className="mt-1.5 w-full text-xs px-2 py-1.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-brand-500">
                  <option value="low">Low</option>
                  <option value="normal">Normal</option>
                  <option value="high">High</option>
                  <option value="urgent">Urgent</option>
                </select>
              </div>

              {/* Due Date */}
              <div>
                <label className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Due Date</label>
                <input type="date" value={task.due_date ? task.due_date.split('T')[0] : ''}
                  onChange={e => handleUpdate('due_date', e.target.value || null)}
                  className="mt-1.5 w-full text-xs px-2 py-1.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-brand-500" />
              </div>

              {/* Stage */}
              <div>
                <label className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Stage</label>
                <p className="mt-1 text-xs text-gray-700">{task.stage_name || 'Unassigned'}</p>
              </div>

              {/* Linked Screen Tab */}
              {task.screen_id && onJumpToTab && (
                <div>
                  <label className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Linked Tab</label>
                  <button
                    onClick={() => { onJumpToTab(task.screen_id); onClose(); }}
                    className="mt-1.5 w-full flex items-center gap-2 px-3 py-2 text-xs font-medium text-brand-600 bg-brand-50 border border-brand-200 rounded-lg hover:bg-brand-100 transition-colors"
                  >
                    <ExternalLink className="h-3.5 w-3.5" />
                    {(() => {
                      const tab = (tabs || []).find(t => t.screen && t.screen.id === task.screen_id);
                      return tab ? (tab.tab_label || tab.screen.name) : 'Go to Tab';
                    })()}
                  </button>
                </div>
              )}

              {/* Category */}
              <div>
                <label className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Category</label>
                <p className="mt-1 text-xs text-gray-700">{(task.category || 'general').replace('_', ' ')}</p>
              </div>

              {/* Created */}
              <div>
                <label className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Created</label>
                <p className="mt-1 text-[10px] text-gray-500">{new Date(task.created_at).toLocaleString()}</p>
              </div>

              {task.completed_at && (
                <div>
                  <label className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Completed</label>
                  <p className="mt-1 text-[10px] text-gray-500">{new Date(task.completed_at).toLocaleString()}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
